import Model from './model';
import { Config, ConsentType, Token } from '../../../types/config';
import { AuthProvider } from '../../../types/common';

export default class Presenter {
  model: Model;

  facebookAppId: string;

  googleAppId: string;

  outsideAppId: string;

  walletConnectId: string | undefined;

  supported_locales: string[];

  oauthOnly: boolean;

  config: Config;

  token: Token;

  selectedAuthProvider: AuthProvider | null = null;

  onNext: (authProvider: AuthProvider) => void;

  onConsentChange: (type: ConsentType, consent: boolean) => void;

  onLanguageChange: (language: string) => void;

  constructor(
    model: Model,
    config: Config,
    onNext: (authProvider: AuthProvider) => void,
    onConsentChange: (type: ConsentType, consent: boolean) => void,
    onLanguageChange: (language: string) => void
  ) {
    this.facebookAppId = config.facebook_app_id;
    this.googleAppId = config.google_app_id;
    this.outsideAppId = config.outside_app_id;
    this.walletConnectId = config.wallet_connect?.project_id;
    this.supported_locales = config.supported_locales;
    this.oauthOnly = config.oauth_only;
    this.config = config;
    this.token = config.token;
    this.onNext = onNext;
    this.onConsentChange = onConsentChange;
    this.onLanguageChange = onLanguageChange;
    this.model = model;
  }

  async onAttach(): Promise<void> {
    const { allow_phone: allowPhone, allow_email: allowEmail } = this.token;

    const baseLanguage = navigator.language.split('-')[0];

    // Use the browser's language, otherwise use the base language (e.g. 'en' for 'en-GB'), otherwise fallback onto the config's default language, otherwise 'en'.
    if (this.supported_locales?.includes(navigator.language)) {
      this.onLanguageChange(navigator.language);
    } else if (this.supported_locales?.includes(baseLanguage)) {
      this.onLanguageChange(baseLanguage);
    } else {
      const defaultLanguage = this.config.default_language;
      let defaultLocale = 'en';
      if (typeof defaultLanguage === 'string') {
        defaultLocale = defaultLanguage;
      } else if (Array.isArray(defaultLanguage) && defaultLanguage.length > 0) {
        [defaultLocale] = defaultLanguage;
      }
      this.onLanguageChange(defaultLocale);
    }

    this.model.allowPhone = allowPhone;
    this.model.allowEmail = allowEmail;
    this.model.showFacebook = !!this.facebookAppId;
    this.model.showGoogle = !!this.googleAppId;
    this.model.showOutsideOauth = !!this.outsideAppId;
    this.model.showWalletConnect = !!this.walletConnectId;
    this.model.oauthOnly = !!this.oauthOnly;

    const {
      default: defaultConsent,
      marketing: marketingConsent,
      custom: customConsent,
    } = this.config.claim.consent ?? {};

    this.model.showMarketingConsent = marketingConsent?.enabled ?? false;
    this.model.marketingConsentTerms = marketingConsent?.terms ?? '';
    this.model.showCustomConsent = customConsent?.enabled ?? false;
    this.model.showDefaultConsent = defaultConsent?.enabled ?? false;
  }

  onAuthProviderClick(provider: AuthProvider): void {
    this.selectedAuthProvider = provider;
    if (provider !== 'token') {
      this.openConsentPopup(provider);
    } else {
      this.onNext(provider);
    }
  }

  openConsentPopup(authProvider: AuthProvider): void {
    this.selectedAuthProvider = authProvider;
    this.model.isConsentPopupOpen = true;
  }

  closeConsentPopup(): void {
    this.model.isConsentPopupOpen = false;
  }

  onConsentPopupContinue(): void {
    this.onNext(this.selectedAuthProvider!);
  }
}
